import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Visibility,
  Edit,
  Search,
  Add,
  Home as HomeIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setLoading as setToolsLoading,
  setError as setToolsError,
  setTools,
  setSelectedTool,
  setPage as setToolsPage,
  setPageSize as setToolsPageSize,
  setSearch as setToolsSearch,
  clearSelectedTool,
  updateTool as updateToolAction,
  resetAgentTools,
} from '../../store/slices/agentToolsSlice';
import {
  setLoading as setPromptsLoading,
  setError as setPromptsError,
  setPrompts,
  setSelectedPrompt,
  setPage as setPromptsPage,
  setPageSize as setPromptsPageSize,
  setSearch as setPromptsSearch,
  clearSelectedPrompt,
  addPrompt as addPromptAction,
  updatePrompt as updatePromptAction,
  deletePrompt as deletePromptAction,
  resetAgentPrompts,
} from '../../store/slices/agentPromptsSlice';
import { GET_AGENT_TOOLS_LIST, GET_AGENT_TOOL } from '../../graphql/queries/agentTools.queries';
import { UPDATE_AGENT_TOOL } from '../../graphql/mutations/agentTools.mutations';
import { GET_AGENT_PROMPTS_LIST, GET_AGENT_PROMPT } from '../../graphql/queries/agentPrompts.queries';
import { CREATE_AGENT_PROMPT, UPDATE_AGENT_PROMPT, DELETE_AGENT_PROMPT } from '../../graphql/mutations/agentPrompts.mutations';
import { GET_AGENT_CONFIGURATION } from '../../graphql/queries/agentConfigurations.queries';
import {
  FindAgentToolListInput,
  FindAgentToolInput,
  UpdateAgentToolInput,
  UpdateAgentToolResponse,
  AgentToolsListResponse,
  AgentToolResponse,
  AgentTool,
} from '../../types/agentTools.types';
import {
  FindAgentPromptListInput,
  FindAgentPromptInput,
  CreateAgentPromptInput,
  UpdateAgentPromptInput,
  DeleteAgentPromptInput,
  AgentPromptsListResponse,
  AgentPromptResponse,
  CreateAgentPromptResponse,
  UpdateAgentPromptResponse,
  DeleteAgentPromptResponse,
  AgentPrompt,
} from '../../types/agentPrompts.types';
import {
  AgentConfigurationResponse,
  FindAgentConfigurationInput,
  AgentConfiguration as AgentConfigurationType,
} from '../../types/agentConfigurations.types';
import ViewAgentToolModal from '../../components/AgentTools/ViewAgentToolModal';
import EditAgentToolModal, {
  AgentToolFormData,
} from '../../components/AgentTools/EditAgentToolModal';
import ViewAgentPromptModal from '../../components/AgentPrompts/ViewAgentPromptModal';
import CreateEditAgentPromptModal, {
  AgentPromptFormData,
} from '../../components/AgentPrompts/CreateEditAgentPromptModal';
import ConfirmDeleteAgentPromptDialog from '../../components/AgentPrompts/ConfirmDeleteAgentPromptDialog';
import MainLayout from '../../components/Layout/MainLayout';
import './AgentConfiguration.css';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`agent-config-tabpanel-${index}`}
      aria-labelledby={`agent-config-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const AgentConfiguration: React.FC = () => {
  const navigate = useNavigate();
  const { agentConfigId } = useParams<{ agentConfigId: string }>();
  const dispatch = useAppDispatch();

  const { tools, loading: toolsLoading, error: toolsError, pagination: toolsPagination, search: toolsSearch } =
    useAppSelector((state) => state.agentTools);
  const {
    prompts,
    loading: promptsLoading,
    error: promptsError,
    pagination: promptsPagination,
    search: promptsSearch,
  } = useAppSelector((state) => state.agentPrompts);

  // General state
  const [tabValue, setTabValue] = useState(0);
  const [agentInfo, setAgentInfo] = useState<AgentConfigurationType | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Tools tab state
  const [toolSearchInput, setToolSearchInput] = useState(toolsSearch);
  const [viewToolModalOpen, setViewToolModalOpen] = useState(false);
  const [editToolModalOpen, setEditToolModalOpen] = useState(false);
  const [toolSaving, setToolSaving] = useState(false);
  const [toolSaveError, setToolSaveError] = useState<string | null>(null);
  const [editToolData, setEditToolData] = useState<AgentToolFormData | null>(null);

  // Prompts tab state
  const [promptSearchInput, setPromptSearchInput] = useState(promptsSearch);
  const [viewPromptModalOpen, setViewPromptModalOpen] = useState(false);
  const [createEditPromptModalOpen, setCreateEditPromptModalOpen] = useState(false);
  const [confirmDeletePromptOpen, setConfirmDeletePromptOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState<AgentPrompt | null>(null);
  const [promptModalMode, setPromptModalMode] = useState<'create' | 'edit'>('create');
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptDeleting, setPromptDeleting] = useState(false);
  const [promptSaveError, setPromptSaveError] = useState<string | null>(null);
  const [promptDeleteError, setPromptDeleteError] = useState<string | null>(null);
  const [editPromptData, setEditPromptData] = useState<AgentPromptFormData | null>(null);

  // === Queries & Mutations - Tools ===
  const [fetchAgentConfig] = useLazyQuery<
    { agentConfiguration: AgentConfigurationResponse },
    { params: FindAgentConfigurationInput }
  >(GET_AGENT_CONFIGURATION, { fetchPolicy: 'network-only' });

  const [fetchTools, { loading: fetchingToolsList }] = useLazyQuery<
    { agentTools: AgentToolsListResponse },
    { params: FindAgentToolListInput }
  >(GET_AGENT_TOOLS_LIST, { fetchPolicy: 'network-only' });

  const [fetchTool] = useLazyQuery<
    { agentTool: AgentToolResponse },
    { params: FindAgentToolInput }
  >(GET_AGENT_TOOL, { fetchPolicy: 'network-only' });

  const [updateAgentToolMutation] = useMutation<
    { updateAgentTool: UpdateAgentToolResponse },
    { params: UpdateAgentToolInput }
  >(UPDATE_AGENT_TOOL);

  // === Queries & Mutations - Prompts ===
  const [fetchPrompts, { loading: fetchingPromptsList }] = useLazyQuery<
    { agentPrompts: AgentPromptsListResponse },
    { params: FindAgentPromptListInput }
  >(GET_AGENT_PROMPTS_LIST, { fetchPolicy: 'network-only' });

  const [fetchPrompt] = useLazyQuery<
    { agentPrompt: AgentPromptResponse },
    { params: FindAgentPromptInput }
  >(GET_AGENT_PROMPT, { fetchPolicy: 'network-only' });

  const [createAgentPromptMutation] = useMutation<
    { createAgentPrompt: CreateAgentPromptResponse },
    { params: CreateAgentPromptInput }
  >(CREATE_AGENT_PROMPT);

  const [updateAgentPromptMutation] = useMutation<
    { updateAgentPrompt: UpdateAgentPromptResponse },
    { params: UpdateAgentPromptInput }
  >(UPDATE_AGENT_PROMPT);

  const [deleteAgentPromptMutation] = useMutation<
    { deleteAgentPrompt: DeleteAgentPromptResponse },
    { params: DeleteAgentPromptInput }
  >(DELETE_AGENT_PROMPT);

  // === Effects ===
  useEffect(() => {
    if (agentConfigId) {
      loadAgentInfo();
    }
    return () => {
      dispatch(resetAgentTools());
      dispatch(resetAgentPrompts());
    };
  }, [agentConfigId]);

  useEffect(() => {
    if (agentConfigId) {
      loadTools();
    }
  }, [agentConfigId, toolsPagination.page, toolsPagination.pageSize, toolsSearch]);

  useEffect(() => {
    if (agentConfigId) {
      loadPrompts();
    }
  }, [agentConfigId, promptsPagination.page, promptsPagination.pageSize, promptsSearch]);

  // === Loaders ===
  const loadAgentInfo = async () => {
    if (!agentConfigId) return;
    try {
      const { data } = await fetchAgentConfig({
        variables: { params: { ids: [agentConfigId] } },
      });
      if (data?.agentConfiguration?.success && data.agentConfiguration.data && data.agentConfiguration.data.length > 0) {
        setAgentInfo(data.agentConfiguration.data[0]);
      }
    } catch (err: any) {
      console.error('Error loading agent info:', err);
    }
  };

  const loadTools = async () => {
    if (!agentConfigId) return;
    try {
      dispatch(setToolsLoading(true));
      const { data } = await fetchTools({
        variables: {
          params: {
            agentConfigId,
            search: toolsSearch || null,
            page: toolsPagination.page,
            pageSize: toolsPagination.pageSize,
          },
        },
      });
      if (data?.agentTools?.success && data.agentTools.data) {
        dispatch(setTools(data.agentTools.data));
      } else {
        dispatch(setToolsError(data?.agentTools?.message || 'Error al cargar herramientas'));
      }
    } catch (err: any) {
      dispatch(setToolsError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setToolsLoading(false));
    }
  };

  const loadPrompts = async () => {
    if (!agentConfigId) return;
    try {
      dispatch(setPromptsLoading(true));
      const { data } = await fetchPrompts({
        variables: {
          params: {
            agentConfigId,
            search: promptsSearch || null,
            page: promptsPagination.page,
            pageSize: promptsPagination.pageSize,
          },
        },
      });
      if (data?.agentPrompts?.success && data.agentPrompts.data) {
        dispatch(setPrompts(data.agentPrompts.data));
      } else {
        dispatch(setPromptsError(data?.agentPrompts?.message || 'Error al cargar prompts'));
      }
    } catch (err: any) {
      dispatch(setPromptsError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setPromptsLoading(false));
    }
  };

  // === Tools handlers ===
  const handleToolSearch = () => {
    dispatch(setToolsSearch(toolSearchInput));
  };

  const handleToolSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleToolSearch();
  };

  const handleToolPaginationChange = (model: GridPaginationModel) => {
    dispatch(setToolsPage(model.page + 1));
    dispatch(setToolsPageSize(model.pageSize));
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewTool = async (id: string) => {
    try {
      dispatch(setToolsLoading(true));
      const { data } = await fetchTool({ variables: { params: { ids: [id] } } });
      if (data?.agentTool?.success && data.agentTool.data && data.agentTool.data.length > 0) {
        dispatch(setSelectedTool(data.agentTool.data[0]));
        setViewToolModalOpen(true);
      } else {
        dispatch(setToolsError(data?.agentTool?.message || 'Error al cargar herramienta'));
      }
    } catch (err: any) {
      dispatch(setToolsError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setToolsLoading(false));
    }
  };

  const handleEditTool = async (id: string) => {
    try {
      dispatch(setToolsLoading(true));
      const { data } = await fetchTool({ variables: { params: { ids: [id] } } });
      if (data?.agentTool?.success && data.agentTool.data && data.agentTool.data.length > 0) {
        const tool = data.agentTool.data[0];
        dispatch(setSelectedTool(tool));
        setEditToolData({
          id: tool.id,
          toolKey: tool.toolKey,
          description: tool.description,
        });
        setToolSaveError(null);
        setEditToolModalOpen(true);
      } else {
        dispatch(setToolsError(data?.agentTool?.message || 'Error al cargar herramienta'));
      }
    } catch (err: any) {
      dispatch(setToolsError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setToolsLoading(false));
    }
  };

  const handleSaveTool = async (formData: AgentToolFormData) => {
    try {
      setToolSaving(true);
      setToolSaveError(null);

      const { data: responseData } = await updateAgentToolMutation({
        variables: {
          params: {
            id: formData.id,
            toolKey: formData.toolKey,
            description: formData.description,
          },
        },
      });

      if (responseData?.updateAgentTool?.success) {
        if (responseData.updateAgentTool.data) {
          dispatch(updateToolAction(responseData.updateAgentTool.data));
        }
        setEditToolModalOpen(false);
        dispatch(clearSelectedTool());
        setToolSaveError(null);
        setEditToolData(null);
        setSuccessMessage('Herramienta actualizada exitosamente');
      } else {
        const msg = responseData?.updateAgentTool?.message || 'Error al actualizar herramienta';
        const errs = responseData?.updateAgentTool?.errors || [];
        setToolSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setToolSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setToolSaving(false);
    }
  };

  const handleCloseViewToolModal = () => {
    setViewToolModalOpen(false);
    dispatch(clearSelectedTool());
  };

  const handleCloseEditToolModal = () => {
    setEditToolModalOpen(false);
    dispatch(clearSelectedTool());
    setToolSaveError(null);
    setToolSaving(false);
    setEditToolData(null);
  };

  // === Prompts handlers ===
  const handlePromptSearch = () => {
    dispatch(setPromptsSearch(promptSearchInput));
  };

  const handlePromptSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handlePromptSearch();
  };

  const handlePromptPaginationChange = (model: GridPaginationModel) => {
    dispatch(setPromptsPage(model.page + 1));
    dispatch(setPromptsPageSize(model.pageSize));
  };

  const serializeJsonField = (value: string): any => {
    if (!value || value.trim() === '') return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  const deserializeJsonField = (value: any): string => {
    if (value == null) return '';
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return '';
    }
  };

  const handleViewPrompt = async (id: string) => {
    try {
      dispatch(setPromptsLoading(true));
      const { data } = await fetchPrompt({ variables: { params: { ids: [id] } } });
      if (data?.agentPrompt?.success && data.agentPrompt.data && data.agentPrompt.data.length > 0) {
        dispatch(setSelectedPrompt(data.agentPrompt.data[0]));
        setViewPromptModalOpen(true);
      } else {
        dispatch(setPromptsError(data?.agentPrompt?.message || 'Error al cargar prompt'));
      }
    } catch (err: any) {
      dispatch(setPromptsError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setPromptsLoading(false));
    }
  };

  const handleEditPrompt = async (id: string) => {
    try {
      dispatch(setPromptsLoading(true));
      const { data } = await fetchPrompt({ variables: { params: { ids: [id] } } });
      if (data?.agentPrompt?.success && data.agentPrompt.data && data.agentPrompt.data.length > 0) {
        const prompt = data.agentPrompt.data[0];
        dispatch(setSelectedPrompt(prompt));
        setEditPromptData({
          id: prompt.id,
          agentConfigId: prompt.agentConfigId,
          businessDomain: prompt.businessDomain,
          systemRole: prompt.systemRole,
          mainTask: prompt.mainTask,
          instructions: prompt.instructions || '',
          restrictions: prompt.restrictions || '',
          responseFormatRules: prompt.responseFormatRules || '',
          promptInput: prompt.promptInput || '',
          contextVariables: deserializeJsonField(prompt.contextVariables),
          fewShotExamples: deserializeJsonField(prompt.fewShotExamples),
          isActive: prompt.isActive,
        });
        setPromptModalMode('edit');
        setPromptSaveError(null);
        setCreateEditPromptModalOpen(true);
      } else {
        dispatch(setPromptsError(data?.agentPrompt?.message || 'Error al cargar prompt'));
      }
    } catch (err: any) {
      dispatch(setPromptsError(err.message || 'Error de conexión'));
    } finally {
      dispatch(setPromptsLoading(false));
    }
  };

  const handleCreatePrompt = () => {
    dispatch(clearSelectedPrompt());
    setPromptModalMode('create');
    setPromptSaveError(null);
    setEditPromptData(null);
    setCreateEditPromptModalOpen(true);
  };

  const handleSavePrompt = async (formData: AgentPromptFormData) => {
    try {
      setPromptSaving(true);
      setPromptSaveError(null);

      const contextVariables = serializeJsonField(formData.contextVariables);
      const fewShotExamples = serializeJsonField(formData.fewShotExamples);

      if (promptModalMode === 'create') {
        const { data: responseData } = await createAgentPromptMutation({
          variables: {
            params: {
              agentConfigId: agentConfigId!,
              businessDomain: formData.businessDomain,
              systemRole: formData.systemRole,
              mainTask: formData.mainTask,
              instructions: formData.instructions,
              restrictions: formData.restrictions,
              responseFormatRules: formData.responseFormatRules,
              promptInput: formData.promptInput || null,
              contextVariables,
              fewShotExamples,
              isActive: formData.isActive,
            },
          },
        });

        if (responseData?.createAgentPrompt?.success) {
          if (responseData.createAgentPrompt.data) {
            dispatch(addPromptAction(responseData.createAgentPrompt.data));
          }
          setCreateEditPromptModalOpen(false);
          dispatch(clearSelectedPrompt());
          setPromptSaveError(null);
          setEditPromptData(null);
          setSuccessMessage('Prompt creado exitosamente');
        } else {
          const msg = responseData?.createAgentPrompt?.message || 'Error al crear prompt';
          const errs = responseData?.createAgentPrompt?.errors || [];
          setPromptSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      } else {
        if (!formData.id) {
          setPromptSaveError('ID de prompt no disponible');
          return;
        }

        const { data: responseData } = await updateAgentPromptMutation({
          variables: {
            params: {
              id: formData.id,
              agentConfigId: formData.agentConfigId,
              businessDomain: formData.businessDomain,
              systemRole: formData.systemRole,
              mainTask: formData.mainTask,
              instructions: formData.instructions,
              restrictions: formData.restrictions,
              responseFormatRules: formData.responseFormatRules,
              promptInput: formData.promptInput || null,
              contextVariables,
              fewShotExamples,
              isActive: formData.isActive,
            },
          },
        });

        if (responseData?.updateAgentPrompt?.success) {
          if (responseData.updateAgentPrompt.data) {
            dispatch(updatePromptAction(responseData.updateAgentPrompt.data));
          }
          setCreateEditPromptModalOpen(false);
          dispatch(clearSelectedPrompt());
          setPromptSaveError(null);
          setEditPromptData(null);
          setSuccessMessage('Prompt actualizado exitosamente');
        } else {
          const msg = responseData?.updateAgentPrompt?.message || 'Error al actualizar prompt';
          const errs = responseData?.updateAgentPrompt?.errors || [];
          setPromptSaveError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
        }
      }
    } catch (err: any) {
      setPromptSaveError(err.message || 'Error de conexión con el servidor');
    } finally {
      setPromptSaving(false);
    }
  };

  const handleCloseViewPromptModal = () => {
    setViewPromptModalOpen(false);
    dispatch(clearSelectedPrompt());
  };

  const handleCloseCreateEditPromptModal = () => {
    setCreateEditPromptModalOpen(false);
    dispatch(clearSelectedPrompt());
    setPromptSaveError(null);
    setPromptSaving(false);
    setEditPromptData(null);
  };

  const handleDeletePromptClick = (prompt: AgentPrompt) => {
    setPromptToDelete(prompt);
    setConfirmDeletePromptOpen(true);
    setPromptDeleteError(null);
  };

  const handleCancelDeletePrompt = () => {
    setConfirmDeletePromptOpen(false);
    setPromptToDelete(null);
    setPromptDeleteError(null);
  };

  const handleConfirmDeletePrompt = async () => {
    if (!promptToDelete) return;
    try {
      setPromptDeleting(true);
      setPromptDeleteError(null);
      const { data: responseData } = await deleteAgentPromptMutation({
        variables: { params: { id: promptToDelete.id } },
      });
      if (responseData?.deleteAgentPrompt?.success) {
        dispatch(deletePromptAction(promptToDelete.id));
        setConfirmDeletePromptOpen(false);
        setPromptToDelete(null);
        setSuccessMessage('Prompt eliminado exitosamente');
      } else {
        const msg = responseData?.deleteAgentPrompt?.message || 'Error al eliminar prompt';
        const errs = responseData?.deleteAgentPrompt?.errors || [];
        setPromptDeleteError(errs.length > 0 ? `${msg}: ${errs.join(', ')}` : msg);
      }
    } catch (err: any) {
      setPromptDeleteError(err.message || 'Error de conexión con el servidor');
    } finally {
      setPromptDeleting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };

  // === Grid column definitions ===
  const toolGridColumns: GridColDef<AgentTool>[] = [
    { field: 'toolKey', headerName: 'Clave de Herramienta', flex: 1, minWidth: 200 },
    { field: 'description', headerName: 'Descripción', flex: 2, minWidth: 300 },
    { field: 'createdAt', headerName: 'Fecha Creación', width: 130 },
    { field: 'createdBy', headerName: 'Creado Por', width: 130 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton size="small" color="primary" onClick={() => handleViewTool(params.row.id)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="secondary" onClick={() => handleEditTool(params.row.id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const promptGridColumns: GridColDef<AgentPrompt>[] = [
    { field: 'businessDomain', headerName: 'Dominio', width: 120 },
    { field: 'systemRole', headerName: 'Rol del Sistema', flex: 1, minWidth: 200 },
    { field: 'mainTask', headerName: 'Tarea Principal', flex: 1, minWidth: 200 },
    {
      field: 'isActive',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.isActive ? 'Activo' : 'Inactivo'}
          color={params.row.isActive ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    { field: 'createdAt', headerName: 'Fecha Creación', width: 130 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver">
            <IconButton size="small" color="primary" onClick={() => handleViewPrompt(params.row.id)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="secondary" onClick={() => handleEditPrompt(params.row.id)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => handleDeletePromptClick(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="agent-config-container">
        <Paper elevation={2} className="agent-config-paper">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              color="inherit"
              onClick={() => navigate('/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
              Inicio
            </Link>
            <Link underline="hover" color="inherit" sx={{ cursor: 'pointer' }} onClick={() => navigate('/agent-configurations')}>
              Agentes
            </Link>
            <Typography color="text.primary">Configuración de Agente</Typography>
          </Breadcrumbs>

          <Box className="agent-config-header">
            <Typography variant="h4" component="h1">
              Configuración de Agente
            </Typography>
            {agentInfo && (
              <Typography variant="body2" color="text.secondary">
                {agentInfo.agentKey} — {agentInfo.provider} / {agentInfo.modelName}
              </Typography>
            )}
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Herramientas" id="agent-config-tab-0" aria-controls="agent-config-tabpanel-0" />
              <Tab label="Prompts" id="agent-config-tab-1" aria-controls="agent-config-tabpanel-1" />
            </Tabs>
          </Box>

          {/* ==================== HERRAMIENTAS TAB ==================== */}
          <TabPanel value={tabValue} index={0}>
            <Box className="agent-config-toolbar">
              <Box className="search-box">
                <TextField
                  size="small"
                  placeholder="Buscar herramienta..."
                  value={toolSearchInput}
                  onChange={(e) => setToolSearchInput(e.target.value)}
                  onKeyPress={handleToolSearchKeyPress}
                  sx={{ minWidth: 250 }}
                />
                <Button variant="contained" startIcon={<Search />} onClick={handleToolSearch} sx={{ ml: 1 }}>
                  Buscar
                </Button>
              </Box>
            </Box>

            {toolsError && (
              <Box className="error-box">
                <Typography color="error">{toolsError}</Typography>
              </Box>
            )}

            <Box className="data-grid-container">
              <DataGrid
                rows={tools}
                columns={toolGridColumns}
                loading={toolsLoading || fetchingToolsList}
                pageSizeOptions={[5, 10, 25, 50]}
                paginationModel={{ page: toolsPagination.page - 1, pageSize: toolsPagination.pageSize }}
                onPaginationModelChange={handleToolPaginationChange}
                paginationMode="server"
                rowCount={toolsPagination.totalRecords}
                disableRowSelectionOnClick
                autoHeight
                sx={{
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
              />
            </Box>
          </TabPanel>

          {/* ==================== PROMPTS TAB ==================== */}
          <TabPanel value={tabValue} index={1}>
            <Box className="agent-config-toolbar">
              <Box className="search-box">
                <TextField
                  size="small"
                  placeholder="Buscar prompt..."
                  value={promptSearchInput}
                  onChange={(e) => setPromptSearchInput(e.target.value)}
                  onKeyPress={handlePromptSearchKeyPress}
                  sx={{ minWidth: 250 }}
                />
                <Button variant="contained" startIcon={<Search />} onClick={handlePromptSearch} sx={{ ml: 1 }}>
                  Buscar
                </Button>
              </Box>

              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreatePrompt}
                sx={{ backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' } }}
              >
                Nuevo Prompt
              </Button>
            </Box>

            {promptsError && (
              <Box className="error-box">
                <Typography color="error">{promptsError}</Typography>
              </Box>
            )}

            <Box className="data-grid-container">
              <DataGrid
                rows={prompts}
                columns={promptGridColumns}
                loading={promptsLoading || fetchingPromptsList}
                pageSizeOptions={[5, 10, 25, 50]}
                paginationModel={{ page: promptsPagination.page - 1, pageSize: promptsPagination.pageSize }}
                onPaginationModelChange={handlePromptPaginationChange}
                paginationMode="server"
                rowCount={promptsPagination.totalRecords}
                disableRowSelectionOnClick
                autoHeight
                sx={{
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                }}
              />
            </Box>
          </TabPanel>
        </Paper>

        {/* Tool Modals */}
        <ViewAgentToolModal open={viewToolModalOpen} onClose={handleCloseViewToolModal} />
        <EditAgentToolModal
          open={editToolModalOpen}
          onClose={handleCloseEditToolModal}
          onSave={handleSaveTool}
          saving={toolSaving}
          saveError={toolSaveError}
          initialData={editToolData}
        />

        {/* Prompt Modals */}
        <ViewAgentPromptModal open={viewPromptModalOpen} onClose={handleCloseViewPromptModal} />
        <CreateEditAgentPromptModal
          open={createEditPromptModalOpen}
          onClose={handleCloseCreateEditPromptModal}
          onSave={handleSavePrompt}
          mode={promptModalMode}
          saving={promptSaving}
          saveError={promptSaveError}
          agentConfigId={agentConfigId || ''}
          initialData={editPromptData}
        />
        <ConfirmDeleteAgentPromptDialog
          open={confirmDeletePromptOpen}
          onClose={handleCancelDeletePrompt}
          onConfirm={handleConfirmDeletePrompt}
          promptDescription={promptToDelete?.businessDomain || ''}
          deleting={promptDeleting}
        />

        {/* Success Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>

        {/* Error Snackbars */}
        {promptDeleteError && (
          <Snackbar open={!!promptDeleteError} autoHideDuration={6000} onClose={() => setPromptDeleteError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <Alert onClose={() => setPromptDeleteError(null)} severity="error" sx={{ width: '100%' }}>{promptDeleteError}</Alert>
          </Snackbar>
        )}
      </div>
    </MainLayout>
  );
};

export default AgentConfiguration;
